// ./src/pages/private/WorkflowsPage/WorkflowsPage.tsx
import React, { useState, useCallback, useEffect } from "react";
import { Stack, Alert, Text } from "@mantine/core";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { IconAlertCircle } from "@tabler/icons-react";
import { PageProps } from "@sdflc/react-router-pages";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
  PremiumFeatureGate,
  FeatureBenefitsList,
} from "@/components/generic";
import { WorkflowTable, WorkflowEditDrawer } from "@/components/features";
import { useWorkflows, useNumericFeature } from "@/hooks";
import { Workflow, WorkflowInput } from "@/utils/api";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE, OPERATION_RIGHTS, FEATURE_CODES } from "@/config";
import { ENTITIES } from "@/config/userRoles";

// ---------------------------------------------------------------------------
// Route shapes this page is mounted at (see sitemap):
//
//   /workflows               — list view, drawer closed
//   /workflows/add           — list view, drawer open in create mode
//   /workflows/:id/edit      — list view, drawer open in edit mode
//
// Navigation to /workflows/:id/items is handled by handleGoInWorkflow and
// renders a separate page component (WorkflowItemsPage).
// ---------------------------------------------------------------------------

const WORKFLOWS_BENEFITS = [
  "Define exactly what happens after every form submission — automatically",
  "Set conditions based on field values to route, filter, or block submissions",
  "Send targeted notifications to different recipients based on what was submitted",
  "Forward submissions to webhooks, Slack, CRMs, and cloud storage via connectors",
  "Block spam or unwanted submissions with Reject rules before they reach your systems",
  "Chain multiple actions in a single ordered flow with full branching control",
];

export const WorkflowsPage: React.FC<PageProps> = (props: PageProps) => {
  const { user, hasRightTo } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id?: string }>();

  // Check if workflows feature is enabled
  const { isEnabled: isWorkflowsEnabled } = useNumericFeature(
    FEATURE_CODES.MAX_WORKFLOWS,
  );

  // ------------------------------------------------------------------
  // Derive UI mode from the current URL — no local "modalOpen" state.
  // The URL is the single source of truth for which drawer is showing.
  // ------------------------------------------------------------------
  const workflowIdFromUrl = params.id;
  const isAddMode = location.pathname.endsWith("/add");
  const isEditMode = !!workflowIdFromUrl && location.pathname.endsWith("/edit");
  const isDrawerOpen = isAddMode || isEditMode;

  const canCreate = hasRightTo(ENTITIES.WORKFLOWS, OPERATION_RIGHTS.CREATE);

  // ------------------------------------------------------------------
  // Hook — owns all data and remote operations
  // ------------------------------------------------------------------
  const {
    workflows,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    isCreating,
    isUpdating,
    removingIds,
    lastEditedId,
    lastError,
    list,
    loadMore,
    refresh,
    get,
    create,
    update,
    remove,
    clearError,
  } = useWorkflows({
    autoLoad: isWorkflowsEnabled,
    userId: user?.id,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // ------------------------------------------------------------------
  // Local UI state — only things that have no business being in the hook
  // ------------------------------------------------------------------

  // The workflow staged for deletion (drives the confirm modal)
  const [workflowToDelete, setWorkflowToDelete] = useState<Workflow | null>(
    null,
  );

  // The workflow being edited — populated from the list or from a get()
  // call; null when the drawer is in create mode or is closed
  const [editingWorkflow, setEditingWorkflow] = useState<Workflow | null>(null);

  // Search input — kept local because it's purely presentational until
  // the user stops typing (the actual filter is applied via list())
  const [searchValue, setSearchValue] = useState("");

  // Delete error — captured from the remove() OpResult and shown as a
  // dismissible Alert on the page after the confirm modal closes.
  // Kept as a plain string (extracted from OpResult) so it remains
  // readable after the hook's lastError has been cleared by another
  // operation. Cleared whenever a new deletion is staged or dismissed.
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // ------------------------------------------------------------------
  // Load the workflow for edit mode
  //
  // When the user lands directly on /workflows/:id/edit (e.g. via a
  // bookmark or browser back), the item may not be in the list yet.
  // We call get() which merges the result into the list and makes it
  // available for the sync effect below.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!isEditMode || !workflowIdFromUrl) {
      return;
    }

    // Check the list first to avoid a redundant network call
    const alreadyLoaded = workflows.find((w) => w.id === workflowIdFromUrl);
    if (alreadyLoaded) {
      setEditingWorkflow(alreadyLoaded);
      return;
    }

    get(workflowIdFromUrl);
  }, [isEditMode, workflowIdFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentionally omitting `workflows` and `get` — we only want this to
  // fire when the URL params change, not on every list update.

  // ------------------------------------------------------------------
  // Sync editingWorkflow from the list
  //
  // Runs whenever the workflows array updates (initial load, refresh,
  // or after a get()). Keeps the drawer form up to date if the item
  // arrives after the drawer is already open.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!isEditMode || !workflowIdFromUrl) {
      setEditingWorkflow(null);
      return;
    }

    const found = workflows.find((w) => w.id === workflowIdFromUrl);
    if (found) {
      setEditingWorkflow(found);
    }
  }, [isEditMode, workflowIdFromUrl, workflows]);

  // ------------------------------------------------------------------
  // Search
  // ------------------------------------------------------------------
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      const newFilters: Partial<typeof filters> = { ...filters };
      if (value.trim()) {
        newFilters.searchKeyword = value.trim();
      } else {
        delete newFilters.searchKeyword;
      }
      list(newFilters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    },
    [filters, sortBy, list],
  );

  // ------------------------------------------------------------------
  // Sort
  // ------------------------------------------------------------------
  const handleSortChange = useCallback(
    (newSortBy: typeof sortBy) => {
      list(filters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, newSortBy, true);
    },
    [filters, list],
  );

  // ------------------------------------------------------------------
  // Refresh
  // ------------------------------------------------------------------
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // ------------------------------------------------------------------
  // Navigation helpers — drawer open/close is driven by URL changes
  // ------------------------------------------------------------------
  const handleAddWorkflow = useCallback(() => {
    clearError();
    navigate("/workflows/add");
  }, [navigate, clearError]);

  const handleEditWorkflow = useCallback(
    (workflow: Workflow) => {
      clearError();
      if (workflow.id) {
        navigate(`/workflows/${workflow.id}/edit`);
      }
    },
    [navigate, clearError],
  );

  const handleDrawerClose = useCallback(() => {
    clearError();
    setEditingWorkflow(null);
    navigate("/workflows");
  }, [navigate, clearError]);

  // ------------------------------------------------------------------
  // Duplicate — pre-populate the create drawer with source data.
  // The user can rename before saving; no API call happens here.
  // ------------------------------------------------------------------
  const handleDuplicateWorkflow = useCallback(
    (source: Workflow) => {
      clearError();
      // Set editingWorkflow so the drawer form is pre-populated, but
      // navigate to /add so the drawer submits a create (not an update).
      setEditingWorkflow({
        ...source,
        id: undefined,
        workflowName: source.workflowName
          ? `Copy of ${source.workflowName}`
          : undefined,
      });
      navigate("/workflows/add");
    },
    [navigate, clearError],
  );

  // ------------------------------------------------------------------
  // Go in — navigate to the workflow items page
  // ------------------------------------------------------------------
  const handleGoInWorkflow = useCallback(
    (workflow: Workflow) => {
      if (workflow.id) {
        navigate(`/workflows/${workflow.id}/items`);
      }
    },
    [navigate],
  );

  // ------------------------------------------------------------------
  // Remove (single) — stage for confirmation, then execute
  // ------------------------------------------------------------------
  const handleRemoveWorkflowClick = useCallback((workflow: Workflow) => {
    // Clear any previous delete error before showing the modal for a new item
    setDeleteError(null);
    setWorkflowToDelete(workflow);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setWorkflowToDelete(null);
    // Keep deleteError visible after the modal closes — the user may want
    // to read why the previous attempt failed. It will be cleared the next
    // time they open the confirm modal for any item.
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!workflowToDelete?.id) {
      return;
    }

    const result = await remove(workflowToDelete.id);

    if (result.didSucceed()) {
      setWorkflowToDelete(null);
      setDeleteError(null);

      // If the deleted item was open in the drawer, navigate back to the list
      if (workflowIdFromUrl === workflowToDelete.id) {
        navigate("/workflows");
      }
    } else {
      // Extract the error message now, while the result is fresh.
      // We store it as a string so it persists even after the hook's
      // lastError is overwritten by a subsequent operation.
      const msg =
        result.getErrorSummary() ||
        result.getErrorSummary("") ||
        "Could not delete the workflow. Please try again.";
      setDeleteError(msg);

      // Close the confirm modal — the error surfaces as a page Alert
      // so the user can read it without a modal blocking the screen.
      setWorkflowToDelete(null);
    }
  }, [workflowToDelete, remove, workflowIdFromUrl, navigate]);

  // ------------------------------------------------------------------
  // Create
  // ------------------------------------------------------------------
  const handleCreateWorkflow = useCallback(
    async (params: WorkflowInput) => {
      const result = await create(params);
      if (result.didSucceed()) {
        // lastEditedId is set in the hook — no local state needed.
        // Navigate away to close the drawer; the row highlight persists.
        navigate("/workflows");
      }
      // On failure the drawer stays open; it reads lastError directly.
      return result;
    },
    [create, navigate],
  );

  // ------------------------------------------------------------------
  // Update
  // ------------------------------------------------------------------
  const handleUpdateWorkflow = useCallback(
    async (id: string, params: WorkflowInput) => {
      const result = await update(id, params);
      if (result.didSucceed()) {
        navigate("/workflows");
      }
      return result;
    },
    [update, navigate],
  );

  // ------------------------------------------------------------------
  // Error scoping
  //
  // lastError from the hook is shown as a list-level Alert only when the
  // drawer is closed — when the drawer is open it owns lastError and
  // shows it inline. This prevents the same error appearing in two places.
  //
  // deleteError is a separate local string, shown as its own Alert
  // below the toolbar. It survives hook state changes because it was
  // extracted from the OpResult at the moment of failure.
  // ------------------------------------------------------------------
  const listError =
    !isDrawerOpen && lastError ? lastError.getErrorSummary() : null;

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <DashboardLayout {...props}>
      <PremiumFeatureGate
        featureCode={FEATURE_CODES.MAX_WORKFLOWS}
        featureTitle="Workflow Automation"
        forceEnabled={isWorkflowsEnabled}
        featureDescription={
          <FeatureBenefitsList benefits={WORKFLOWS_BENEFITS} />
        }
      >
        <Stack gap="md">
          {/* Toolbar — add, refresh, search */}
          <AppToolbar
            preset="page-header"
            sections={[
              {
                id: "actions",
                items: [
                  <AppButton
                    key="add-workflow"
                    iconName="Plus"
                    onClick={handleAddWorkflow}
                    disabled={isCreating || loading || !canCreate}
                  >
                    Add
                  </AppButton>,
                  <AppButton
                    key="refresh"
                    variant="secondary"
                    iconName="Refresh"
                    onClick={handleRefresh}
                    disabled={isCreating || loading}
                  />,
                ],
                align: "left",
                gap: "sm",
              },
              {
                id: "search",
                items: [
                  <SearchInput
                    key="search"
                    placeholder="Search workflows..."
                    value={searchValue}
                    onChange={handleSearchChange}
                    style={{ minWidth: 250 }}
                  />,
                ],
                align: "right",
              },
            ]}
          />

          {/* List-level error (load / refresh failures) */}
          {listError && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              variant="light"
              title="Could not load workflows"
              withCloseButton
              onClose={clearError}
            >
              <Text size="sm">{listError}</Text>
            </Alert>
          )}

          {/* Delete error — shown after a failed removal once the confirm modal closes */}
          {deleteError && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              variant="light"
              title="Could not delete workflow"
              withCloseButton
              onClose={() => setDeleteError(null)}
            >
              <Text size="sm">{deleteError}</Text>
            </Alert>
          )}

          {/* Table */}
          <WorkflowTable
            workflows={workflows}
            loading={loading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            sortBy={sortBy}
            onSortChange={handleSortChange}
            onLoadMore={loadMore}
            onEditWorkflow={handleEditWorkflow}
            onDuplicateWorkflow={handleDuplicateWorkflow}
            onGoInWorkflow={handleGoInWorkflow}
            onRemoveWorkflow={handleRemoveWorkflowClick}
            removingIds={removingIds}
            lastEditedId={lastEditedId}
          />

          {/* Empty state */}
          <EmptyState
            display={isEmpty && !loading}
            title="No workflows yet"
            description="Create workflows to automate and standardise your processes"
            actionIcon="Plus"
            actionLabel="Add Workflow"
            onAction={handleAddWorkflow}
          />
        </Stack>

        {/* ----------------------------------------------------------------
            Create / Edit drawer
            isDrawerOpen is derived from the URL — opening and closing is
            always a navigate() call, never a boolean toggle.
            The workflow prop covers both edit mode (editingWorkflow from the
            list) and duplicate mode (editingWorkflow pre-populated by
            handleDuplicateWorkflow but with id stripped).
            lastError is scoped to the drawer only when it is open.
        ---------------------------------------------------------------- */}
        <WorkflowEditDrawer
          opened={isDrawerOpen}
          onClose={handleDrawerClose}
          workflow={editingWorkflow}
          onCreateWorkflow={handleCreateWorkflow}
          onUpdateWorkflow={handleUpdateWorkflow}
          isCreating={isCreating}
          isUpdating={isUpdating}
          lastError={isDrawerOpen ? lastError : null}
          onClearError={clearError}
        />

        {/* ----------------------------------------------------------------
            Delete confirmation modal
            ConfirmModal has no error prop — on failure we close the modal
            and surface the error as a dismissible page Alert (deleteError).
            confirmInProgress drives the spinner via removingIds.has().
        ---------------------------------------------------------------- */}
        <ConfirmModal
          opened={!!workflowToDelete}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Delete Workflow"
          message={`Are you sure you want to delete "${workflowToDelete?.workflowName}"?`}
          details="This action cannot be undone. All workflow items associated with this workflow will also be removed."
          confirmInProgress={
            !!workflowToDelete?.id && removingIds.has(workflowToDelete.id)
          }
          confirmLabel="Delete"
          type="danger"
        />
      </PremiumFeatureGate>
    </DashboardLayout>
  );
};
